import type {
  AppendixCodeItemOption,
  AppendixColumnDataType,
  AppendixDetail,
  CodeItemGroup,
  CreateAppendixPayload,
} from '../types/codeIndex'

// Draft shapes used while an appendix table is being authored. Cells are keyed by column key so
// reordering or removing a column never shifts values between columns.
export type AppendixColumnDraft = {
  key: string
  // Saved ids and fields the editor does not expose, sent back on update.
  id?: string | null
  length?: number | null
  label: string
  dataType: AppendixColumnDataType
  unit: string
  codeItemCode: string
}

export type AppendixCellMeta = {
  id: string | null
  notes: string | null
}

export type AppendixRowDraft = {
  key: string
  id?: string | null
  cells: Record<string, string>
  cellMeta?: Record<string, AppendixCellMeta>
}

// Saved appendix fields kept as they were loaded when an existing appendix is updated.
export type SavedAppendixTableMeta = {
  description: string
  tableId: string | null
  tableDescription: string
}

export const APPENDIX_COLUMN_TYPE_OPTIONS: Array<{ value: AppendixColumnDataType; label: string }> = [
  { value: 'Text', label: 'Text' },
  { value: 'String', label: 'String' },
  { value: 'Number', label: 'Number' },
  { value: 'Boolean', label: 'Boolean' },
  { value: 'CodeItem', label: 'Code item' },
]

export const NUMBER_CELL_ERROR = 'This column holds numbers. Enter a number, or change the column to text.'
export const BOOLEAN_CELL_ERROR = 'This column holds Yes or No values. Choose Yes or No.'
export const EMPTY_CELL_ERROR = 'Every cell needs a value.'

const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/

export function getColumnTypeLabel(dataType: AppendixColumnDataType): string {
  return APPENDIX_COLUMN_TYPE_OPTIONS.find(option => option.value === dataType)?.label || dataType
}

// Lowercase noun used in sentences ("Turndown is now a number column").
export function getColumnTypeNoun(dataType: AppendixColumnDataType): string {
  return getColumnTypeLabel(dataType).toLowerCase()
}

export function columnHasUnit(dataType: AppendixColumnDataType): boolean {
  return dataType === 'Number' || dataType === 'String'
}

let draftKeySequence = 0

export function createDraftKey(prefix: string): string {
  draftKeySequence += 1
  return `${prefix}-${Date.now().toString(36)}-${draftKeySequence}`
}

export function createColumnDraft(): AppendixColumnDraft {
  return { key: createDraftKey('column'), label: '', dataType: 'Text', unit: '', codeItemCode: '' }
}

export function createRowDraft(columns: AppendixColumnDraft[]): AppendixRowDraft {
  return {
    key: createDraftKey('row'),
    cells: Object.fromEntries(columns.map(column => [column.key, ''])),
  }
}

export function buildAppendixCodeItemOptions(groups: CodeItemGroup[]): AppendixCodeItemOption[] {
  return groups
    .filter(group => Boolean(group.id))
    .map(group => ({
      id: String(group.id),
      code: group.codeItemPos.code,
      title: group.title || '',
      values: group.rows
        .filter(row => Boolean(row.id))
        .map(row => ({
          id: String(row.id),
          code: row.optionCode,
          description: row.description || '',
        })),
    }))
}

export function findCodeItemOption(
  codeItems: AppendixCodeItemOption[],
  code: string | null | undefined
): AppendixCodeItemOption | null {
  if (!code) return null
  return codeItems.find(codeItem => codeItem.code === code) || null
}

function isValueOfCodeItem(value: string, codeItem: AppendixCodeItemOption): boolean {
  return codeItem.values.some(option => option.code === value)
}

export function validateCell(
  column: Pick<AppendixColumnDraft, 'dataType'>,
  value: string | null | undefined,
  codeItem: AppendixCodeItemOption | null
): string | null {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return EMPTY_CELL_ERROR

  switch (column.dataType) {
    case 'Number':
      return NUMBER_PATTERN.test(trimmed) ? null : NUMBER_CELL_ERROR
    case 'Boolean':
      return trimmed === 'true' || trimmed === 'false' ? null : BOOLEAN_CELL_ERROR
    case 'CodeItem':
      if (!codeItem) return 'Choose which code item this column references.'
      return isValueOfCodeItem(trimmed, codeItem) ? null : `Choose a value of code item ${codeItem.code}.`
    default:
      return null
  }
}

export type AppendixColumnError = {
  field: 'label' | 'codeItem'
  message: string
}

export function validateColumns(columns: AppendixColumnDraft[]): Record<string, AppendixColumnError> {
  const errors: Record<string, AppendixColumnError> = {}
  const seenLabels = new Set<string>()

  columns.forEach(column => {
    const label = column.label.trim()
    if (!label) {
      errors[column.key] = { field: 'label', message: 'Every column needs a label.' }
      return
    }

    const normalizedLabel = label.toLowerCase()
    if (seenLabels.has(normalizedLabel)) {
      errors[column.key] = { field: 'label', message: `There is more than one column named ${label}.` }
      return
    }
    seenLabels.add(normalizedLabel)

    if (column.dataType === 'CodeItem' && !column.codeItemCode) {
      errors[column.key] = { field: 'codeItem', message: `Choose which code item ${label} references.` }
    }
  })

  return errors
}

// A cell survives a type change when its value is still valid for the new type. Empty cells and
// code item columns without a referenced code item yet have nothing to clear.
export function isCellClearedByTypeChange(
  value: string | null | undefined,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null
): boolean {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return false
  if (nextType === 'CodeItem' && !nextCodeItem) return false
  if (nextType === 'Text' || nextType === 'String') return false

  return validateCell({ dataType: nextType }, trimmed, nextCodeItem) !== null
}

export function findCellsClearedByTypeChange(
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null
): string[] {
  return rows
    .map(row => row.cells[columnKey] ?? '')
    .filter(value => isCellClearedByTypeChange(value, nextType, nextCodeItem))
}

export function countCellsClearedByTypeChange(
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null
): number {
  return findCellsClearedByTypeChange(rows, columnKey, nextType, nextCodeItem).length
}

export function applyColumnTypeChange(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null
): { columns: AppendixColumnDraft[]; rows: AppendixRowDraft[]; clearedCount: number } {
  let clearedCount = 0

  const nextColumns = columns.map(column => column.key === columnKey
    ? {
        ...column,
        dataType: nextType,
        unit: columnHasUnit(nextType) ? column.unit : '',
        codeItemCode: nextType === 'CodeItem' ? nextCodeItem?.code || '' : '',
      }
    : column)

  const nextRows = rows.map(row => {
    if (!isCellClearedByTypeChange(row.cells[columnKey], nextType, nextCodeItem)) return row
    clearedCount += 1
    return { ...row, cells: { ...row.cells, [columnKey]: '' } }
  })

  return { columns: nextColumns, rows: nextRows, clearedCount }
}

export function removeColumn(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string
): { columns: AppendixColumnDraft[]; rows: AppendixRowDraft[]; removedCount: number } {
  let removedCount = 0

  const nextRows = rows.map(row => {
    const { [columnKey]: removedValue, ...cells } = row.cells
    if ((removedValue ?? '').trim()) removedCount += 1
    if (!row.cellMeta) return { ...row, cells }
    const { [columnKey]: _removedMeta, ...cellMeta } = row.cellMeta
    return { ...row, cells, cellMeta }
  })

  return {
    columns: columns.filter(column => column.key !== columnKey),
    rows: nextRows,
    removedCount,
  }
}

export function moveItem<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items
  }

  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

const APPENDIX_COLUMN_DATA_TYPES = new Set<string>(APPENDIX_COLUMN_TYPE_OPTIONS.map(option => option.value))

// Builds the editor draft from a loaded appendix. Only the first table is edited; cells map to
// columns by column position.
export function hydrateAppendixDraft(detail: AppendixDetail): {
  columns: AppendixColumnDraft[]
  rows: AppendixRowDraft[]
  meta: SavedAppendixTableMeta
} {
  const [table] = detail.tables
  const meta: SavedAppendixTableMeta = {
    description: detail.description || '',
    tableId: table?.id || null,
    tableDescription: table?.description || '',
  }
  if (!table) return { columns: [], rows: [], meta }

  const sortedColumns = [...table.columns].sort((a, b) => a.position - b.position)
  const columns: AppendixColumnDraft[] = sortedColumns.map(column => {
    const dataType = column.dataType && APPENDIX_COLUMN_DATA_TYPES.has(column.dataType) ? column.dataType : 'Text'
    return {
      key: createDraftKey('column'),
      id: column.id,
      length: column.length,
      label: column.title || column.name || '',
      dataType,
      unit: column.unit || '',
      codeItemCode: column.codeItemData || '',
    }
  })
  const columnKeyByPosition = new Map(sortedColumns.map((column, index) => [column.position, columns[index].key]))

  const rows: AppendixRowDraft[] = [...table.rows]
    .sort((a, b) => a.position - b.position)
    .map(row => {
      const cells: Record<string, string> = Object.fromEntries(columns.map(column => [column.key, '']))
      const cellMeta: Record<string, AppendixCellMeta> = {}
      row.cells.forEach(cell => {
        const columnKey = columnKeyByPosition.get(cell.columnPosition)
        if (!columnKey) return
        cells[columnKey] = cell.value ?? ''
        cellMeta[columnKey] = { id: cell.id, notes: cell.notes }
      })
      return { key: createDraftKey('row'), id: row.id, cells, cellMeta }
    })

  return { columns, rows, meta }
}

export function buildAppendixPayload(
  name: string,
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  saved?: SavedAppendixTableMeta
): CreateAppendixPayload {
  const title = name.trim()
  // Creates send no ids; updates send the loaded ids, with null for anything added since.
  const withId = <T extends object>(id: string | null | undefined, body: T) => saved ? { id: id ?? null, ...body } : body

  return {
    title,
    description: saved?.description ?? '',
    tables: [withId(saved?.tableId, {
      name: title,
      description: saved?.tableDescription ?? '',
      columns: columns.map((column, position) => {
        const label = column.label.trim()
        const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
        return withId(column.id, {
          name: label,
          title: label,
          dataType: column.dataType,
          ...(saved ? { length: column.length ?? null } : {}),
          unit: unit || null,
          position,
          codeItemData: column.dataType === 'CodeItem' ? column.codeItemCode || null : null,
        })
      }),
      rows: rows.map((row, position) => withId(row.id, {
        position,
        cells: columns.map((column, columnPosition) => {
          const meta = row.cellMeta?.[column.key]
          return withId(meta?.id, {
            value: (row.cells[column.key] ?? '').trim(),
            notes: meta?.notes ?? null,
            columnPosition,
          })
        }),
      })),
    })],
  }
}

export function formatColumnPillMeta(column: Pick<AppendixColumnDraft, 'dataType' | 'unit'>): string {
  const typeLabel = getColumnTypeLabel(column.dataType)
  const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
  return unit ? `${typeLabel} - ${unit}` : typeLabel
}

export function formatColumnHeader(column: Pick<AppendixColumnDraft, 'label' | 'dataType' | 'unit'>, fallback: string): string {
  const label = column.label.trim() || fallback
  const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
  return unit ? `${label} - ${unit}` : label
}

export function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}

// Phrases describing values that don't fit a column type, e.g. "31 rows hold values that aren't numbers".
export function describeInvalidValues(
  dataType: AppendixColumnDataType,
  codeItemCode: string,
  count: number
): string {
  const single = count === 1
  switch (dataType) {
    case 'Number':
      return single ? 'a number' : 'numbers'
    case 'Boolean':
      return 'true or false'
    case 'CodeItem':
      return single ? `a value of code item ${codeItemCode}` : `values of code item ${codeItemCode}`
    default:
      return single ? `a ${getColumnTypeNoun(dataType)}` : `${getColumnTypeNoun(dataType)} values`
  }
}

export function formatValueExamples(values: string[], limit = 2): string {
  const distinct = Array.from(new Set(values.map(value => value.trim()).filter(Boolean))).slice(0, limit)
  return distinct.join(' and ')
}
